import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "../../../shared/Icon";
import { MakingChrome } from "../access/FoundationChrome";
import type { PrintBook } from "./print-book";
import "../shared/next-base.css";
import "../library/library.css";
import "../library/library-live.css";
import "./print.css";

/**
 * The printed-copy pages' frame (accepted R77 print shell) in the signed-in making chrome (Stage 2,
 * F1): the making bar names the step and keeps the one way back to where the reader came from.
 * The redesign claims the site chrome for these pages.
 */
export function PrintShell({
  title,
  back,
  book,
  children,
}: {
  title: string;
  back: { to: string; label: string };
  /** The book being printed, when the page has it: the bar names it and Help is about it. */
  book?: { id: string; title: string };
  children: ReactNode;
}) {
  const { bookId: routeBookId } = useParams<{ bookId?: string }>();
  useEffect(() => {
    document.title = `${title} · Life’s Capsule`;
  }, [title]);
  const bookId = book?.id ?? routeBookId;
  return (
    <div className="customer-design-system personal-library print-experience">
      <MakingChrome
        task={{
          ...(bookId ? { bookId } : {}),
          ...(book ? { title: book.title } : {}),
          step: title,
          exit: back,
        }}
      />
      <main id="main-content" className="print-main" tabIndex={-1}>
        {children}
      </main>
      <footer className="print-footer">
        <span>Your book. Your words. Made to keep.</span>
      </footer>
    </div>
  );
}

/** A fact about the book being printed, shown only when the service supplied it. */
export type PrintFact = { label: string; value: ReactNode };

/** Stable readable fallback while one cover-only renewal is attempted. */
export function PrintCover({
  book,
  fallbackClassName = "print-book-cover__plain",
}: {
  book: PrintBook;
  fallbackClassName?: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  return book.cover && failed !== book.cover ? (
    <img
      src={book.cover}
      alt={"Cover of " + book.title}
      referrerPolicy="no-referrer"
      onError={() => {
        setFailed(book.cover);
        book.onCoverError?.();
      }}
    />
  ) : (
    <span className={fallbackClassName}>{book.title}</span>
  );
}

/**
 * The book selected for printing (accepted R77 book summary): its cover, title and kind with the
 * facts the service gave. On a phone the facts fold under "Book details and files"; opening them
 * eases in over 180ms unless motion is reduced, the page is hidden or the layout changes.
 */
export function PrintBookSummary({
  book,
  edition,
  facts,
  productFacts,
  readTo,
  children,
}: {
  book: PrintBook;
  /** "Approved print edition", "Edition under review"…: which version this is. */
  edition: string;
  facts: PrintFact[];
  /** The engine's product facts (`PhysicalFacts`), when they could be read. */
  productFacts?: ReactNode;
  readTo: string;
  /** The files and their fingerprints, kept behind their own disclosure. */
  children?: ReactNode;
}) {
  const content = useRef<HTMLDivElement>(null);
  const animation = useRef<Animation | null>(null);
  useEffect(() => {
    const phone = matchMedia("(max-width: 700px)");
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const stop = () => {
      animation.current?.cancel();
      animation.current = null;
    };
    const hidden = () => {
      if (document.hidden) stop();
    };
    phone.addEventListener("change", stop);
    reduced.addEventListener("change", stop);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      stop();
      phone.removeEventListener("change", stop);
      reduced.removeEventListener("change", stop);
      document.removeEventListener("visibilitychange", hidden);
    };
  }, []);
  function toggle(event: SyntheticEvent<HTMLDetailsElement>) {
    animation.current?.cancel();
    if (
      event.currentTarget.open &&
      content.current &&
      !document.hidden &&
      matchMedia("(max-width: 700px)").matches &&
      !matchMedia("(prefers-reduced-motion: reduce)").matches &&
      typeof content.current.animate === "function"
    )
      animation.current = content.current.animate(
        [
          { opacity: 0.8, clipPath: "inset(0 0 6px 0)" },
          { opacity: 1, clipPath: "inset(0 0 0 0)" },
        ],
        { duration: 180, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
      );
  }
  const details = (
    <>
      {facts.length > 0 && (
        <dl className="print-facts">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {productFacts}
      <Link className="print-read-link" to={readTo}>
        Take another look inside
        <Icon name="arrow" size={16} />
      </Link>
      {children}
    </>
  );
  return (
    <aside className="print-book" aria-label="Book selected for printing">
      <Link to={readTo} className="print-book-cover">
        <PrintCover key={book.id} book={book} />
      </Link>
      <h2>{book.title}</h2>
      <p className="print-book-type">
        {book.type} · {edition}
      </p>
      <div className="print-book-desktop-details">{details}</div>
      <details className="print-book-details" onToggle={toggle}>
        <summary>Book details and files</summary>
        <div ref={content} className="print-book-details-content">
          {details}
        </div>
      </details>
    </aside>
  );
}

/** A page-level state with nothing to review yet (accepted R77 empty states). */
export function PrintEmpty({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="print-empty">
      <h1 tabIndex={-1}>{title}</h1>
      {children}
    </section>
  );
}
